import { toast } from 'react-toastify';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, IconButton, Menu, MenuItem, Chip, FormControl, InputLabel, Select, FormControlLabel, Switch, List, ListItem, ListItemText, ListItemSecondaryAction, Divider, Typography, Checkbox, FormGroup, Accordion, AccordionSummary, AccordionDetails, Paper } from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, FilterList as FilterIcon, ViewColumn as ColumnIcon, ExpandMore as ExpandMoreIcon, Save as SaveIcon, Share as ShareIcon, Star as StarIcon, StarBorder as StarBorderIcon, DragIndicator as DragIndicatorIcon, Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon, Search as SearchIcon, ArrowUpward as ArrowUpwardIcon, ArrowDownward as ArrowDownwardIcon, Sort as SortIcon } from '@mui/icons-material';

import constants from '../../constants';
import { formatProperties } from '../../utils/propertyFormatter';
import { getViews, createView, updateView, deleteView, getOrCreateDefaultView } from '../../store/Objects';

const ViewManager = ({ objectType, onViewChange, activeViewId }) => {
  const dispatch = useDispatch();
  const objectData = useSelector(state => state['Objects']?.[objectType]);
  const views = objectData?.['views'] || [];
  const defaultView = objectData?.['defaultView'];
  const propertiesData = objectData?.['properties'] || {};

  // Check if there's a default view in the views list
  const existingDefaultView = views.find(view => view['isDefault']);

  // Format properties using the utility function
  const properties = formatProperties(propertiesData, objectType);
  const objectSchema = constants.getObjectSchema(objectType);

  const [anchorEl, setAnchorEl] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingView, setEditingView] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const [propertySearch, setPropertySearch] = useState('');
  const [showAddColumn, setShowAddColumn] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState([]); // For managing columns with order
  const [viewData, setViewData] = useState({ name: '', description: '', isDefault: false, isShared: false, filters: [], columns: [], sortBy: { field: 'createdAt', order: 'desc' } });

  useEffect(() => { dispatch(getViews({ objectType })) }, [dispatch, objectType]);

  // Set the default view when views are loaded
  useEffect(() => {
    if (views.length > 0 && !activeViewId) {
      const defaultViewFromList = views.find(view => view['isDefault']);
      if (defaultViewFromList) {
        onViewChange(defaultViewFromList);
      } else {
        // If no default view exists, try to get or create one
        dispatch(getOrCreateDefaultView({
          objectType,
          successCallback: (view) => {
            if (view) {
              onViewChange(view);
            }
          }
        }));
      }
    }
  }, [views, activeViewId, objectType]);

  const handleMenuOpen = (event) => {
    setAnchorEl(event['currentTarget']);
    // If no active view, get or create default view
    if (!activeViewId) dispatch(getOrCreateDefaultView({ objectType, successCallback: (view) => onViewChange(view) }));
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleCreateView = () => {
    setEditingView(null);
    setViewData({ name: '', description: '', isDefault: false, isShared: false, filters: [], columns: [], sortBy: { field: 'createdAt', order: 'desc' } });
    setDialogOpen(true);
    handleMenuClose();
  };

  const handleEditView = (view) => {
    setEditingView(view);
    // Keep the full column data for editing
    const columnsWithOrder = view['columns'] ? [...view['columns']].sort((a, b) => a['order'] - b['order']) : [];
    // Ensure all columns have required properties
    const formattedColumns = columnsWithOrder.map((col, index) => ({
      propertyName: col['propertyName'],
      // width: col['width'] || 200,
      order: col['order'] !== undefined ? col['order'] : index,
      isVisible: col['isVisible'] !== undefined ? col['isVisible'] : true,
      isSorted: col['isSorted'] || false,
      sortDirection: col['sortDirection'] || 'asc'
    }));
    setSelectedColumns(formattedColumns);

    // Convert backend column format to simple array for frontend
    const simpleColumns = formattedColumns.filter(col => col['isVisible']).map(col => col['propertyName']);

    setViewData({ name: view['name'], description: view['description'] || '', isDefault: view['isDefault'] || false, isShared: view['viewType'] === 'team', filters: view['filters'] || [], columns: simpleColumns, sortBy: view['columns']?.find(col => col['isSorted']) ? { field: view['columns'].find(col => col['isSorted'])['propertyName'], order: view['columns'].find(col => col['isSorted'])['sortDirection'] } : { field: 'createdAt', order: 'desc' } });
    setDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteView = (viewId) => {
    if (!window.confirm('Are you sure you want to delete this view?')) return handleMenuClose();

    dispatch(deleteView({
      objectType,
      viewId,
      successCallback: () => { if (activeViewId === viewId) onViewChange(null); },
      failCallback: () => {
        // Toast is already shown by saga via EventBus
      }
    }));
    handleMenuClose();
  };

  const handleSaveView = () => {
    if (!viewData['name'].trim()) return toast.error('View name is required');

    let formattedColumns;

    if (editingView && selectedColumns.length > 0) {
      // Use the edited columns with their properties
      formattedColumns = selectedColumns.map((col, index) => ({ ...col, order: index }));
    } else if (!editingView && viewData['columns'].length > 0) {
      // Convert columns from simple array to backend format for new views
      formattedColumns = viewData['columns'].map((col, index) => ({ propertyName: col, width: 200, order: index, isVisible: true, isSorted: false, sortDirection: 'asc' }));
    } else {
      // Default columns if nothing selected
      formattedColumns = getDefaultColumns();
    }

    // Ensure columns have correct order
    formattedColumns.forEach((col, index) => { col['order'] = index; });

    // Format view data to match backend expectations
    const formattedViewData = {
      name: viewData['name'],
      description: viewData['description'] || '',
      columns: formattedColumns,
      filters: viewData['filters'] || [],
      viewType: viewData['isShared'] ? 'team' : 'personal',
      isDefault: viewData['isDefault'] || false,
      displaySettings: { pageSize: 50, density: 'comfortable', showGridLines: true, stickyHeader: true }
    };

    // Add objectType only for new views
    if (!editingView) formattedViewData['objectType'] = objectType;

    const action = editingView ? updateView : createView;
    const payload = {
      objectType,
      ...(editingView ? { viewId: editingView['_id'], updates: formattedViewData } : { view: formattedViewData }),
      successCallback: (savedView) => {
        // Toast is already shown by saga via EventBus
        setDialogOpen(false);
        setSelectedColumns([]);
        setShowAddColumn(false);
        setPropertySearch('');
        // Refresh views after save
        dispatch(getViews({ objectType }));
        if (editingView) onViewChange(savedView || editingView);
      },
      failCallback: (error) => {
        console.error('Failed to save view:', error);
        toast.error(`Failed to ${editingView ? 'update' : 'create'} view`);
      }
    };

    dispatch(action(payload));
  };

  // Helper function to get default columns based on object type
  const getDefaultColumns = () => {
    const defaultColumnsByType = { 
      contacts: ['firstName', 'lastName', 'email', 'phone', 'createdAt'], 
      companies: ['name', 'domain', 'industry', 'size', 'createdAt'], 
      deals: ['title', 'amount', 'stage', 'closeDate', 'ownerId'] 
    };

    const defaultCols = defaultColumnsByType[objectType] || ['createdAt', 'updatedAt'];
    return defaultCols.map((col, index) => ({ propertyName: col, width: 200, order: index, isVisible: true, isSorted: false, sortDirection: 'asc' }));
  };

  const handleSetDefault = (viewId) => {
    const view = views.find(v => v['_id'] === viewId);
    if (!view) return;

    dispatch(updateView({
      objectType,
      viewId,
      updates: { ...view, isDefault: true },
      successCallback: () => toast.success('Default view updated')
    }));
  };

  // Drag and drop handlers for column reordering
  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedItem === null) return;

    const draggedColumn = selectedColumns[draggedItem];
    const newColumns = [...selectedColumns];
    newColumns.splice(draggedItem, 1);
    newColumns.splice(dropIndex, 0, draggedColumn);

    setSelectedColumns(newColumns);
    setDraggedItem(null);
  };

  const toggleColumnVisibility = (index) => {
    const newColumns = [...selectedColumns];
    newColumns[index] = { ...newColumns[index], isVisible: !newColumns[index]['isVisible'] };
    setSelectedColumns(newColumns);
  };

  const toggleColumnSort = (index) => {
    const newColumns = [...selectedColumns];

    // If this column is already sorted, toggle direction
    if (newColumns[index]['isSorted']) {
      newColumns[index] = { ...newColumns[index], sortDirection: newColumns[index]['sortDirection'] === 'asc' ? 'desc' : 'asc' };
    } else {
      // Remove sort from all other columns
      newColumns.forEach((col, i) => {
        if (i === index) newColumns[i] = { ...col, isSorted: true, sortDirection: 'asc' };
        else newColumns[i] = { ...col, isSorted: false };
      });
    }

    setSelectedColumns(newColumns);
  };

  const handleColumnAdd = (propertyName) => {
    const property = properties.find(p => p['name'] === propertyName);
    if (!property) return;

    // Check if property already exists
    if (selectedColumns.some(col => col['propertyName'] === propertyName)) {
      toast.info('This column is already added');
      return;
    }

    const newColumn = { propertyName: property['name'], width: 200, order: selectedColumns.length, isVisible: true, isSorted: false, sortDirection: 'asc' };
    setSelectedColumns([...selectedColumns, newColumn]);
    setViewData({ ...viewData, columns: [...viewData['columns'], propertyName] });
    setPropertySearch('');
  };

  const handleColumnRemove = (index) => {
    const newColumns = selectedColumns.filter((_, i) => i !== index);
    setSelectedColumns(newColumns);
    setViewData({ ...viewData, columns: newColumns.filter(col => col['isVisible']).map(col => col['propertyName']) });
  };

  return (
    <>
      <div className='all-contacts'>
        <Button variant="outlined" startIcon={<FilterIcon />} onClick={handleMenuOpen} sx={{ textTransform: 'none' }}>
          {views.find(v => v['_id'] === activeViewId)?.['name'] || `All ${objectSchema?.['plural'] || objectType}`}
        </Button>

        <Menu slotProps={{ paper: { className: "custom-all-contact" } }} anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
          <MenuItem sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <ListItemText primary={`All ${objectSchema?.['plural'] || objectType}`} onClick={() => { onViewChange(null); handleMenuClose(); }} />
            {defaultView && (
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEditView(defaultView); }}>
                <EditIcon fontSize="small" />
              </IconButton>
            )}
          </MenuItem>

          {/* {views.length > 0 && <Divider />} */}

          {views.map((view) => (
            <MenuItem key={view['_id']}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleSetDefault(view['_id']); }}>
                  {view['isDefault'] ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                </IconButton>
                <ListItemText primary={view['name']} secondary={view['isShared'] ? 'Shared' : 'Private'} onClick={() => { onViewChange(view); handleMenuClose(); }} />
              </Box>
              <Box>
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEditView(view); }}><EditIcon fontSize="small" /></IconButton>
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDeleteView(view['_id']); }}><DeleteIcon fontSize="small" /></IconButton>
              </Box>
            </MenuItem>
          ))}

          {/* <Divider /> */}

          <MenuItem onClick={handleCreateView}><AddIcon sx={{ mr: 1 }} />Create new view</MenuItem>
        </Menu>
      </div>

      <Modal isOpen={dialogOpen} toggle={() => { setDialogOpen(false); setSelectedColumns([]); setEditingView(null); setShowAddColumn(false); setPropertySearch(''); }} keyboard={true} modalClassName='right right-without-bg' className={`main-modal right right-side-modal pipeline-modal`}>
        <ModalHeader toggle={() => { setDialogOpen(false); setSelectedColumns([]); setEditingView(null); setShowAddColumn(false); setPropertySearch(''); }}><h3>{editingView ? 'Edit View' : 'Create New View'}</h3></ModalHeader>
        <ModalBody>
          <div className='filters-modal-content'>
            <div className='createdeal-form'>
              <h4>Basic Information</h4>
              <div className='group-form'>
                <label>View Name</label>
                <TextField fullWidth placeholder="Add view name" value={viewData['name']} onChange={(e) => setViewData({ ...viewData, name: e['target']['value'] })} required />
              </div>
              <div className='group-form'>
                <label>Description</label>
                <TextField fullWidth placeholder="Add description" value={viewData['description']} onChange={(e) => setViewData({ ...viewData, description: e['target']['value'] })} multiline rows={2} />
              </div>

              <div className='group-form checkbox-group'>
                <FormControlLabel control={<Switch checked={viewData['isShared']} onChange={(e) => setViewData({ ...viewData, isShared: e['target']['checked'] })} />} label="Share with team" />
                <FormControlLabel control={<Switch checked={viewData['isDefault']} onChange={(e) => setViewData({ ...viewData, isDefault: e['target']['checked'] })} />} label="Set as default view" />
              </div>

              <div className='group-form'>
                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <h4>Columns ({editingView && selectedColumns.length > 0 ? selectedColumns.filter(c => c['isVisible']).length : viewData['columns'].length || 'All'})</h4>
                  </AccordionSummary>
                  <AccordionDetails>
                    {editingView ? (
                      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                        {/* Left side - Add columns */}
                        {/* COMMENTED OUT: Original flex: 1 - making it responsive for mobile */}
                        {/* <Paper className='right-column-add' sx={{ flex: 1, p: 2, }}> */}
                        <Paper className='right-column-add' sx={{ flex: { xs: '1 1 100%', md: 1 }, width: { xs: '100%', md: 'auto' }, p: 2 }}>
                          <h4>Add Columns</h4>
                          <TextField
                            fullWidth
                            size="small"
                            placeholder="Search properties..."
                            value={propertySearch}
                            onChange={(e) => setPropertySearch(e['target']['value'])}
                            sx={{ mb: 2 }}
                            InputProps={{
                              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                            }}
                          />
                          <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                            {(() => {
                              const filteredProps = properties.filter(prop =>
                                prop['label']?.toLowerCase().includes(propertySearch.toLowerCase()) ||
                                prop['name']?.toLowerCase().includes(propertySearch.toLowerCase())
                              );
                              const groupedProps = filteredProps.reduce((acc, prop) => {
                                const group = prop.group || 'Other Properties';
                                if (!acc[group]) acc[group] = [];
                                acc[group].push(prop);
                                return acc;
                              }, {});

                              return Object.entries(groupedProps).map(([groupName, groupProps]) => (
                                <Box key={groupName} sx={{ mb: 2 }}>
                                  <h5>{groupName}</h5>
                                  {groupProps.map((prop) => {
                                    const isAlreadyAdded = selectedColumns.some(col => col['propertyName'] === prop['name']);
                                    return (
                                      <Chip
                                        key={prop['name']}
                                        label={prop['label'] || prop['name']}
                                        onClick={() => !isAlreadyAdded && handleColumnAdd(prop['name'])}
                                        disabled={isAlreadyAdded}
                                        sx={{ m: 0.5 }}
                                        variant={isAlreadyAdded ? "filled" : "outlined"}
                                      />
                                    );
                                  })}
                                </Box>
                              ));
                            })()}
                          </Box>
                        </Paper>

                        {/* Right side - Selected columns */}
                        {/* COMMENTED OUT: Original flex: 1 - making it responsive for mobile */}
                        {/* <Box sx={{ flex: 1 }}> */}
                        <Box sx={{ flex: { xs: '1 1 100%', md: 1 }, width: { xs: '100%', md: 'auto' } }}>
                          <h4>Selected Columns</h4>
                          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                            Drag columns to reorder, use the eye icon to show/hide
                          </Typography>
                          {selectedColumns.length > 0 ? (
                            <List>
                              {selectedColumns.map((column, index) => {
                                const property = properties.find(p => p['name'] === column['propertyName']);
                                return (
                                  <ListItem
                                    key={index}
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, index)}
                                    onDragOver={handleDragOver}
                                    onDrop={(e) => handleDrop(e, index)}
                                    sx={{ border: '1px solid #e0e0e0', borderRadius: 1, mb: 1, backgroundColor: column['isVisible'] ? 'background.paper' : 'action.disabledBackground', cursor: 'move' }}
                                  >
                                    <DragIndicatorIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                    <ListItemText
                                      secondary={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <h4>{property?.['label'] || column['propertyName']}</h4>
                                          <Typography variant="caption">Width:</Typography>
                                          <TextField
                                            size="small"
                                            type="number"
                                            value={column['width']}
                                            onChange={(e) => {
                                              const newColumns = [...selectedColumns];
                                              newColumns[index] = { ...newColumns[index], width: parseInt(e.target.value) || 150 };
                                              setSelectedColumns(newColumns);
                                            }}
                                            inputProps={{ min: 50, max: 500 }}
                                            sx={{ width: 80 }}
                                          />
                                          <Typography variant="caption">px</Typography>
                                        </Box>
                                      }
                                    />
                                    <ListItemSecondaryAction>
                                      {column['isSorted'] && (
                                        <Chip
                                          size="small"
                                          icon={column['sortDirection'] === 'asc' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
                                          label="Sorted"
                                          color="primary"
                                          variant="outlined"
                                        />
                                      )}
                                      <IconButton
                                        edge="end"
                                        onClick={() => toggleColumnSort(index)}
                                        title={column['isSorted'] ? 'Change sort direction' : 'Sort by this column'}
                                      >
                                        {column['isSorted'] ? (
                                          column['sortDirection'] === 'asc' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />
                                        ) : (
                                          <SortIcon />
                                        )}
                                      </IconButton>
                                      <IconButton edge="end" onClick={() => toggleColumnVisibility(index)}>
                                        {column['isVisible'] ? <VisibilityIcon /> : <VisibilityOffIcon />}
                                      </IconButton>
                                      <IconButton edge="end" onClick={() => handleColumnRemove(index)}>
                                        <DeleteIcon />
                                      </IconButton>

                                    </ListItemSecondaryAction>
                                  </ListItem>
                                );
                              })}
                            </List>
                          ) : (
                            <Typography variant="body2" color="textSecondary">
                              No columns selected. Add columns from the right panel.
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ) : (
                      <FormGroup>
                        {properties && properties.length > 0 ? (
                          <>
                            {/* COMMENTED OUT: Original display: flex - making it responsive for mobile */}
                            {/* <Box sx={{ display: 'flex', gap: 2 }}> */}
                            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                              {/* COMMENTED OUT: Original flex: 1 - making it responsive for mobile */}
                              {/* <Box sx={{ flex: 1, p: 2, }}> */}
                              <Box sx={{ flex: { xs: '1 1 100%', md: 1 }, width: { xs: '100%', md: 'auto' }, p: 2 }}>
                                {/* Group properties by their group */}
                                {(() => {
                                  const groupedProps = properties.reduce((acc, prop) => {
                                    const group = prop.group || 'Other Properties';
                                    if (!acc[group]) acc[group] = [];
                                    acc[group].push(prop);
                                    return acc;
                                  }, {});

                                  return Object.entries(groupedProps).map(([groupName, groupProps]) => (
                                    <Box key={groupName} sx={{ mb: 2 }}>
                                      <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                                        {groupName}
                                      </Typography>
                                      {groupProps.map((prop) => (
                                        <FormControlLabel
                                          key={prop['name'] || prop['_id']}
                                          control={
                                            <Checkbox
                                              checked={viewData['columns'].includes(prop['name'])}
                                              onChange={(e) => {
                                                if (e['target']['checked']) setViewData({ ...viewData, columns: [...viewData['columns'], prop['name']] });
                                                else setViewData({ ...viewData, columns: viewData['columns'].filter(c => c !== prop['name']) });
                                              }}
                                            />
                                          }
                                          label={prop['label'] || prop['name']}
                                        />
                                      ))}
                                    </Box>
                                  ));
                                })()}
                              </Box>
                              {/* Right side - Selected columns */}
                              {/* COMMENTED OUT: Original flex: 1 - making it responsive for mobile */}
                              {/* <Box sx={{ flex: 1 }}> */}
                              <Box sx={{ flex: { xs: '1 1 100%', md: 1 }, width: { xs: '100%', md: 'auto' } }}>
                                <h4>Selected Columns</h4>
                                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                  Drag columns to reorder, use the eye icon to show/hide
                                </Typography>
                                {selectedColumns.length > 0 ? (
                                  <List>
                                    {selectedColumns.map((column, index) => {
                                      const property = properties.find(p => p['name'] === column['propertyName']);
                                      return (
                                        <ListItem
                                          key={index}
                                          draggable
                                          onDragStart={(e) => handleDragStart(e, index)}
                                          onDragOver={handleDragOver}
                                          onDrop={(e) => handleDrop(e, index)}
                                          sx={{ border: '1px solid #e0e0e0', borderRadius: 1, mb: 1, backgroundColor: column['isVisible'] ? 'background.paper' : 'action.disabledBackground', cursor: 'move' }}
                                        >
                                          <div className='drag-handle'>
                                            <DragIndicatorIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                            <ListItemText
                                              primary={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                  {property?.['label'] || column['propertyName']}
                                                </Box>
                                              }
                                              secondary={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                  <Typography variant="caption">Width:</Typography>
                                                  <TextField
                                                    size="small"
                                                    type="number"
                                                    value={column['width']}
                                                    onChange={(e) => {
                                                      const newColumns = [...selectedColumns];
                                                      newColumns[index] = { ...newColumns[index], width: parseInt(e.target.value) || 150 };
                                                      setSelectedColumns(newColumns);
                                                    }}
                                                    inputProps={{ min: 50, max: 500 }}
                                                    sx={{ width: 80 }}
                                                  />
                                                  <Typography variant="caption">px</Typography>
                                                </Box>
                                              }
                                            />
                                          </div>


                                          <ListItemSecondaryAction>
                                            {column['isSorted'] && (
                                              <Chip
                                                size="small"
                                                icon={column['sortDirection'] === 'asc' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
                                                label="Sorted"
                                                color="primary"
                                                variant="outlined"
                                              />
                                            )}
                                            <IconButton
                                              edge="end"
                                              onClick={() => toggleColumnSort(index)}
                                              title={column['isSorted'] ? 'Change sort direction' : 'Sort by this column'}
                                            >
                                              {column['isSorted'] ? (
                                                column['sortDirection'] === 'asc' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />
                                              ) : (
                                                <SortIcon />
                                              )}
                                            </IconButton>
                                            <IconButton edge="end" onClick={() => toggleColumnVisibility(index)}>
                                              {column['isVisible'] ? <VisibilityIcon /> : <VisibilityOffIcon />}
                                            </IconButton>
                                            <IconButton edge="end" onClick={() => handleColumnRemove(index)}>
                                              <DeleteIcon />
                                            </IconButton>

                                          </ListItemSecondaryAction>
                                        </ListItem>
                                      );
                                    })}
                                  </List>
                                ) : (
                                  <Typography variant="body2" color="textSecondary">
                                    No columns selected. Add columns from the right panel.
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </>
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            No properties available for this object type
                          </Typography>
                        )}
                      </FormGroup>
                    )}
                  </AccordionDetails>
                </Accordion>
              </div>

              <div className='group-form'>
                <label>Sort By</label>
                <FormControl fullWidth>
                  <Select value={viewData['sortBy']?.['field'] || ''} onChange={(e) => setViewData({ ...viewData, sortBy: { ...viewData['sortBy'], field: e['target']['value'] } })}>
                    <MenuItem value="createdAt">Created Date</MenuItem>
                    <MenuItem value="updatedAt">Last Modified</MenuItem>
                    {properties && properties.length > 0 && properties.map((prop) => (
                      <MenuItem key={prop['name'] || prop['_id']} value={prop['name']}>
                        {prop['label'] || prop['name']}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>

            </div>
            <div className='group-form btn-groups'>
              <button onClick={() => { setDialogOpen(false); setSelectedColumns([]); setEditingView(null); setShowAddColumn(false); setPropertySearch(''); }} className='btn-style-border'>Cancel</button>
              <button onClick={handleSaveView} className='btn-style-new'>
                <SaveIcon /> {editingView ? 'Update' : 'Create'} View
              </button>
            </div>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default ViewManager;